import React, { useCallback, useEffect, useState } from "react";
import {
  Typography,
  Container,
  Button,
  CircularProgress,
  Modal,
  Stack,
  debounce,
  Grid,
  IconButton,
  Chip,
  Drawer,
  TextField,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Autocomplete,
  Tooltip,
} from "@mui/material";

import {
  DragIndicator as DragIndicatorIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
} from "@mui/icons-material";

import { makeStyles } from "@mui/styles";

import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

import { backend, API_URL } from "../../../config";

const useStyles = makeStyles((theme) => ({
  modal: {
    position: "absolute",
    width: 400,
    backgroundColor: "#fff",
    border: "1px solid rgba(0,0,0,.2)",
    borderRadius: ".3rem",
    boxShadow: theme.shadows[5],
    padding: "1rem",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
}));

export const StorefrontBlocks = (props) => {
  const classes = useStyles();

  const blockDataInitialState = {
    id: null,
    title: "",
    products: [],
    display_order: 0,
    active: true,
  };

  const [storefrontBlocks, setStorefrontBlocks] = useState([]);
  const [storefrontBlocksLoaded, setStorefrontBlocksLoaded] = useState(false);
  const [loadingFlag, setLoadingFlag] = useState(false);
  const [finishedFlag, setFinishedFlag] = useState(false);
  const [errorInfo, setErrorInfo] = useState({ error: false, message: "" });
  const [confirmModal, setConfirmModal] = useState(false);
  const [drawerStatus, setDrawerStatus] = useState(false);
  const [blockData, setBlockData] = useState(blockDataInitialState);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productInputValue, setProductInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [productOptions, setProductOptions] = useState([]);
  const [isBlockLoading, setIsBlockLoading] = useState(false);

  const fetchBlockDetails = useCallback(async (block) => {
    console.log("fetch", block);
    // todo: create this backend request to fetch the details from a block including products like we did for categories:
    // list (no product data or just product_count)
    // get/<id> (includes product array)
    setIsBlockLoading(true);

    if (block.products.length === 0) {
      setBlockData(block);
      setIsBlockLoading(false);
      return;
    }

    const blockProductsUPCs = block.products.map((item) => item?.upc || item);
    const productsIdStr = blockProductsUPCs.join("&upc=");

    await backend
      .get(`${API_URL}products?no_page=1&upc=${productsIdStr}`)
      .then(({ data }) => {
        setIsBlockLoading(false);
        setBlockData({
          ...block,
          products: data,
        });
      });
  }, []);

  const toggleDrawer = (e, block = null) => {
    if (block !== null) {
      fetchBlockDetails(block);
    } else {
      setBlockData(blockDataInitialState);
    }

    setDrawerStatus(!drawerStatus);
  };

  const handleChangeProductInput = debounce((e) => {
    const { value } = e.target;
    setProductOptions([]);
    setIsLoading(true);
    setProductInputValue(value);
  }, 300);

  const handleChange = (e, v) => {
    const { checked, name, value } = e.target;

    setBlockData((prevState) => {
      let blockObj = { ...prevState };

      if (["active"].includes(name)) {
        blockObj = { ...blockObj, [name]: checked };
      } else if (name === "item_delete") {
        blockObj = {
          ...blockObj,
          products: blockObj.products.filter((item) => item.upc !== value),
        };
      } else if (name === "products") {
        blockObj = {
          ...blockObj,
          products: [...blockObj.products, value],
        };
      } else {
        blockObj = { ...blockObj, [name]: value };
      }

      return blockObj;
    });
  };

  const sendBlockRequest = async () => {
    const blockPayload = { ...blockData };

    if (blockPayload.id === null) {
      delete blockPayload.id;

      await backend
        .post(`${API_URL}storefrontblocks/`, blockPayload)
        .then(({ data }) => {
          setStorefrontBlocks((prevState) => [...prevState, data]);
          toggleDrawer();
          getStorefrontBlocks();
        })
        .catch((error) => {
          console.log(error);
        });
    } else {
      await backend
        .patch(`${API_URL}storefrontblocks/${blockPayload.id}`, blockPayload)
        .then(({ data }) => {
          const updatedBlocks = storefrontBlocks.map((block) =>
            block.id === data.id ? data : block,
          );
          setStorefrontBlocks(updatedBlocks);
          toggleDrawer();
          getStorefrontBlocks();
        })
        .catch((error) => {
          console.log(error);
        });
    }
  };

  const crudDrawerContent = (
    <div style={{ width: 400, padding: 16 }}>
      <Grid container spacing={2} justifyContent="center" alignItems="center">
        <Grid item xs={12} align="right">
          <Tooltip title="Close">
            <IconButton onClick={toggleDrawer}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Grid>
        <Grid item xs={12}>
          <Typography component="h4">
            <b>
              {blockData?.title
                ? `${blockData?.id ? "Edit" : "Add"} Block: ${blockData.title}`
                : "Add New Block"}
            </b>
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            required={true}
            id="outlined-basic"
            variant="outlined"
            label="Title"
            name="title"
            value={blockData?.title || ""}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            type="number"
            size="small"
            required={true}
            id="outlined-basic"
            variant="outlined"
            label="Display Order"
            name="display_order"
            value={
              blockData.display_order === null ? 99 : blockData.display_order
            }
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} sm={12}>
          <FormControlLabel
            value="start"
            control={
              <Checkbox
                name="active"
                value={blockData?.active || false}
                checked={blockData?.active || false}
                onChange={handleChange}
                color="primary"
              />
            }
            label="Active"
            labelPlacement="end"
          />
        </Grid>
        <Grid item xs={true}>
          <InputLabel shrink id="productLabel">
            Product
          </InputLabel>
          <Autocomplete
            fullWidth
            disablePortal
            size="small"
            name="product_id"
            options={productOptions}
            getOptionLabel={(option) =>
              `${option.name} | (${option.upc}) #${
                option.item_code.item_code || ""
              }#`
            }
            isOptionEqualToValue={(option, value) => option.upc === value.upc}
            filterOptions={(options) => options}
            renderInput={(params) => (
              <TextField {...params} size="small" variant="standard" />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                {`${option.name} | (${option.upc}) #${
                  option.item_code.item_code || ""
                }# Stock: ${option.stock_level || 0} `}
                {["Unit", "Unit / Case"].includes(option.unit_type)
                  ? `Unit: ${option.display_unit_price} `
                  : ""}
                {["Case", "Unit / Case"].includes(option.unit_type)
                  ? `Case: ${option.display_case_price}`
                  : ""}
              </li>
            )}
            getOptionDisabled={(option) => {
              return Boolean(blockData.products.includes(option.upc));
            }}
            loading={isLoading}
            onChange={(e, value) => setSelectedProduct(value)}
            onInputChange={handleChangeProductInput}
          />
        </Grid>
        <Grid item xs={"auto"}>
          <Button
            disabled={selectedProduct === null}
            variant="contained"
            onClick={() => {
              handleChange({
                target: {
                  name: "products",
                  value: selectedProduct,
                },
              });
            }}
          >
            Add
          </Button>
        </Grid>
        <Grid item xs={12}>
          <Typography component="h4">
            <b>List of products</b>
          </Typography>
          <br />
          <div style={{ maxHeight: "50vh", overflow: "auto" }}>
            {isBlockLoading ? (
              <div style={{ display: "flex", justifyContent: "center" }}>
                <CircularProgress size={22} />
              </div>
            ) : blockData.products && blockData.products.length > 0 ? (
              blockData.products.map((item) => (
                <Grid container spacing={0} alignItems="center" key={item.upc}>
                  <Grid item xs={"auto"}>
                    <IconButton aria-label="drag">
                      <DragIndicatorIcon />
                    </IconButton>
                  </Grid>
                  <Grid item xs={true}>
                    {item?.name || "N/A"}
                  </Grid>
                  <Grid item xs={"auto"}>
                    <IconButton
                      aria-label="delete"
                      onClick={() =>
                        handleChange({
                          target: {
                            name: "item_delete",
                            value: item.upc,
                          },
                        })
                      }
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              ))
            ) : (
              <>There are no products assigned</>
            )}
          </div>
        </Grid>
        <Grid item xs={12}>
          <Stack direction="row" spacing={1} justifyContent="right">
            <Button onClick={toggleDrawer}>Cancel</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => sendBlockRequest()}
            >
              Save
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </div>
  );

  const toggleConfirmModal = () => setConfirmModal(!confirmModal);

  const confirmModalContent = (
    <div className={classes.modal}>
      {!loadingFlag && !finishedFlag ? (
        <>
          Are you sure you want to save the storefront blocks arrangement?
          <br />
          <br />
          <div align="right">
            <Button onClick={() => toggleConfirmModal()}>Cancel</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => sendRequest()}
            >
              Confirm
            </Button>
          </div>
        </>
      ) : loadingFlag ? (
        <>
          <div align="center">
            <CircularProgress />
            <div>
              Saving storefront blocks arrangement <br />
              This may take some time relative to the number of blocks
              configured
            </div>
          </div>
          {errorInfo.error ? (
            <>
              <br />
              <br />
              There was an error while saving the blocks arrangement
              <br />
              <br />
              <span
                style={{
                  color: "red",
                  wordBreak: "break-word",
                  maxHeight: 160,
                  overflow: "hidden",
                  display: "inline-block",
                }}
              >
                <b>Error:</b>
                <br />
                {errorInfo.message}
              </span>
              <div align="right">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => resolveError()}
                >
                  Continue
                </Button>
              </div>
            </>
          ) : (
            <></>
          )}
        </>
      ) : (
        <>
          Customer information have been successfully saved
          <br />
          <br />
          <div align="right">
            <Button
              variant="contained"
              color="primary"
              onClick={() => resolveRequest()}
            >
              Close
            </Button>
          </div>
        </>
      )}
    </div>
  );

  const reorderArray = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  const onDragEnd = (result) => {
    if (!result.destination) {
      return;
    }

    if (result.destination.index === result.source.index) {
      return;
    }

    const orderListCatReorder = reorderArray(
      storefrontBlocks,
      result.source.index,
      result.destination.index,
    );

    setStorefrontBlocks(orderListCatReorder);
  };

  const onDelete = (e, blockId) => {
    e.stopPropagation();

    const blocksFiltered = storefrontBlocks.filter(
      (block) => block.id !== blockId,
    );

    setStorefrontBlocks(blocksFiltered);
  };

  const handleBack = () => {
    props.history.push("/storefront");
  };

  const resolveRequest = () => {
    setFinishedFlag(false);
    setLoadingFlag(false);

    toggleConfirmModal();
  };

  const resolveError = () => {
    setFinishedFlag(false);
    setLoadingFlag(false);
    setErrorInfo({ error: false, message: "" });
    toggleConfirmModal();
  };

  const sendRequest = () => {
    setLoadingFlag(true);

    Promise.all(
      storefrontBlocks.map(async (item, index) => {
        const blockPayload = { ...item, display_order: index };

        return await backend
          .patch(`${API_URL}storefrontblocks/${blockPayload.id}`, blockPayload)
          .then(({ data }) => {
            return data;
          })
          .catch((error) => {
            console.log(error);
            setErrorInfo({
              error: true,
              message: JSON.stringify(error.response.data),
            });
          });
      }),
    ).then((result) => {
      console.log(result);

      if (!errorInfo.error) {
        setFinishedFlag(true);
        setLoadingFlag(false);
        getStorefrontBlocks();
      }
    });
  };

  const getStorefrontBlocks = useCallback(async () => {
    await backend
      .get(`${API_URL}storefrontblocks?no_page=1`)
      .then(({ data }) => {
        const dataMap = data.sort((a, b) => a.display_order - b.display_order);

        setStorefrontBlocks(dataMap);
        setStorefrontBlocksLoaded(true);
      })
      .catch((error) => {
        setErrorInfo({ error: true, message: error.message });
        setStorefrontBlocksLoaded(true);
      });
  }, []);

  useEffect(() => {
    const getProductOptions = async () => {
      await backend
        .get(
          `${API_URL}products?no_page=1&warehouse=2&search=${productInputValue}`,
        )
        .then(({ data }) => {
          setProductOptions(data);
          setIsLoading(false);
        });
    };

    if (productInputValue.length >= 3) {
      getProductOptions();
    }
  }, [productInputValue]);

  useEffect(() => {
    if (!storefrontBlocksLoaded) {
      getStorefrontBlocks();
    }
  }, [storefrontBlocksLoaded, getStorefrontBlocks]);

  return (
    <>
      <Container>
        <Modal open={confirmModal} onClose={toggleConfirmModal}>
          {confirmModalContent}
        </Modal>
        <Drawer anchor="right" open={drawerStatus} onClose={toggleDrawer}>
          {crudDrawerContent}
        </Drawer>
        <Typography component="h6" variant="h6" align="center">
          Storefront Blocks
        </Typography>
        <Grid
          container
          spacing={2}
          justifyContent="center"
          alignContent="center"
          sx={{ marginTop: 2 }}
        >
          <Grid item xs={12}>
            <Stack direction="row" spacing={1} justifyContent="right">
              <Button
                variant="contained"
                color="primary"
                onClick={() => toggleDrawer()}
              >
                Add Block
              </Button>
            </Stack>
          </Grid>
          {storefrontBlocks.length > 0 && (
            <Grid item xs={12} sm={12}>
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId={`storefront-blocks-droppable`}>
                  {(provided) => (
                    <Grid
                      container
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                    >
                      {storefrontBlocks.map((block, index) => (
                        <Draggable
                          key={`storefront-block-${block.id}`}
                          draggableId={`storefront-block-${block.id}`}
                          index={index}
                        >
                          {(provided) => (
                            <Grid
                              item
                              xs={12}
                              sm={12}
                              sx={{
                                "&:hover": { backgroundColor: "#eee" },
                              }}
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                            >
                              <Grid container spacing={0} alignItems="center">
                                <Grid item xs={"auto"}>
                                  <IconButton aria-label="drag">
                                    <DragIndicatorIcon />
                                  </IconButton>
                                </Grid>
                                <Grid item xs={true}>
                                  {`${block.title}`}
                                  <Chip
                                    label={block.products.length}
                                    size="small"
                                    sx={{ ml: 1 }}
                                  />
                                </Grid>
                                <Grid item xs={"auto"}>
                                  <Stack direction="row" spacing={1}>
                                    <IconButton
                                      aria-label="edit"
                                      onClick={(e) => toggleDrawer(e, block)}
                                    >
                                      <EditIcon />
                                    </IconButton>
                                    <IconButton
                                      aria-label="delete"
                                      onClick={(e) => onDelete(e, block.id)}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Stack>
                                </Grid>
                              </Grid>
                            </Grid>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </Grid>
                  )}
                </Droppable>
              </DragDropContext>
            </Grid>
          )}
        </Grid>
        <br />
        {!storefrontBlocksLoaded && (
          <div align="center">
            <br />
            <CircularProgress />
            <div>
              Loading information... <br />
            </div>
          </div>
        )}
        {storefrontBlocks.length === 0 && storefrontBlocksLoaded && (
          <div align="center">There are no storefront blocks configured.</div>
        )}
        {storefrontBlocks.length > 0 && (
          <Stack direction="row" spacing={1} justifyContent="right">
            <Button onClick={handleBack}>Cancel</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => toggleConfirmModal()}
            >
              Save Arrangement
            </Button>
          </Stack>
        )}
      </Container>
    </>
  );
};

export default StorefrontBlocks;
